import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation } from 'react-router';
import { ruRU } from '@mui/x-data-grid-pro/locales';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
	getIsCollectionGroupListLoadingSelector,
	getIsCollectionWayListLoadingSelector,
	getCollectionGroupListSelector,
	getCollectionWayListSelector,
} from '../../selectors';
import {
	getCollectionGroupListAction,
	getCollectionWayListAction,
	setGroupOpenAction,
	setWayOpenAction,
} from '../../actions';
import { CustomNoRowsOverlay } from '../../../../components/data-grid/utils';
import { normalizeDataGridModel } from '../../../../utils/normalizeDataGridModel';
import { TABLE_COLLECTION_COLUMNS } from '../../constants';
import { GroupContextComponent } from '../group-context';
import { WayContextComponent } from '../way-context';
import { OrdersComponent } from '../orders';

const CollectionWrapper = ({
	row,
	onGetCollectionGroupList,
	ordersGroupList,
	isCollectionGroupListLoading,
	onGetCollectionWayList,
	ordersWayList,
	isCollectionWayListLoading,
	modes,
	type,
	onSetWayOpen,
	onSetGroupOpen,
}) => {
	const { search } = useLocation();

	const query = new URLSearchParams(search);

	useEffect(() => {
		if (type === 'group') {
			onGetCollectionGroupList({ row, searchQuery: query.toString() });
			onSetGroupOpen(row);
		}

		if (type === 'way') {
			onGetCollectionWayList({ row, searchQuery: query.toString() });
			onSetWayOpen(row);
		}
	}, [type, row.id]);

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('childrenSorts', jsonSorts);

		if (type === 'group') {
			onGetCollectionGroupList({ row, searchQuery: query.toString() });
		}

		if (type === 'way') {
			onGetCollectionWayList({ row, searchQuery: query.toString() });
		}
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			let jsonFilters = [];

			if (type === 'group') {
				jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLLECTION_COLUMNS(modes)));
			}

			if (type === 'way') {
				jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLLECTION_COLUMNS(modes)));
			}

			query.set('childrenFilters', jsonFilters);

			if (type === 'group') {
				onGetCollectionGroupList({ row, searchQuery: query.toString() });
			}

			if (type === 'way') {
				onGetCollectionWayList({ row, searchQuery: query.toString() });
			}
		}
	});

	let list = [];
	let columns = [];
	let isLoading = false;
	let contextComponent = null;
	let defaultColumnVisibility = {};

	if (row.planning) {
		defaultColumnVisibility = {
			address_from: false,
			date_to: false,
			pickup_at: false,
			cost: false,
			comment: false,
		};
	} else {
		defaultColumnVisibility = {
			address_from: false,
			drop_at: false,
			pickup_at: false,
			cost: false,
			comment: false,
		};
	}

	const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibility);

	const [selectedRows, setSelectedRows] = useState({
		type: 'include',
		ids: new Set(),
	});

	const [selectedRow, setSelectedRow] = useState(null);

	const [contextMenu, setContextMenu] = useState(null);

	const handleContextMenu = event => {
		event.preventDefault();

		if (selectedRows.ids.size === 0) {
			setSelectedRow(event.currentTarget.getAttribute('data-id'));
		}

		setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
	};

	const handleClose = () => {
		setContextMenu(null);
	};

	if (type === 'group') {
		list = ordersGroupList ?? [];
		isLoading = Boolean(isCollectionGroupListLoading.find(el => el === row.id));
		columns = TABLE_COLLECTION_COLUMNS(modes);
		contextComponent = (
			<GroupContextComponent
				contextMenu={contextMenu}
				setContextMenu={setContextMenu}
				selectedCollection={selectedRows.ids.size !== 0 ? [...selectedRows.ids] : [selectedRow]}
				group={row}
			/>
		);
	}

	if (type === 'way') {
		list = ordersWayList ?? [];
		isLoading = Boolean(isCollectionWayListLoading.find(el => el === row.id));
		columns = TABLE_COLLECTION_COLUMNS(modes);
		contextComponent = (
			<WayContextComponent
				contextMenu={contextMenu}
				setContextMenu={setContextMenu}
				selectedCollection={selectedRows.ids.size !== 0 ? [...selectedRows.ids] : [selectedRow]}
				way={row}
			/>
		);
	}

	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState();

	const onDetailPanelExpandedRowIdsChange = useCallback(newIds => {
		if (newIds.size > 1) {
			const newSet = [];

			const newIdsArray = [...newIds];
			newSet.push(newIdsArray[newIdsArray.length - 1]);
			setDetailPanelExpandedRowIds(new Set(newSet));
		} else {
			setDetailPanelExpandedRowIds(newIds);
		}
	}, []);

	const getDetailPanelHeight = row => {
		const count = row.count ?? 0;

		if (count === 0) {
			return 300;
		}

		return count > 13 ? 540 : (count + 1) * 40;
	};

	const getDetailPanelContent = useCallback(collection => {
		return <OrdersComponent collection={collection.row} modes={modes} type={type} row={row} />;
	});

	const [columnModel, setColumnModel] = useState({});

	const handleColumnWidthChange = params => {
		setColumnModel(prev => ({
			...prev,
			[params.colDef.field]: params.width,
		}));
	};

	const columnsWithSavedWidth = columns.map(col => ({
		...col,
		width: columnModel[col.field] || col.width,
		flex: columnModel[col.field] ? 0 : col.flex,
	}));

	return (
		<>
			<DataGridPro
				key={`${type} - ${row.id}`}
				slots={{
					noRowsOverlay: CustomNoRowsOverlay,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
					row: {
						onContextMenu: handleContextMenu,
						style: { cursor: 'context-menu' },
					},
				}}
				componentsProps={{
					root: {
						onContextMenu: e => {
							e.preventDefault();
							handleClose();
						},
					},
				}}
				initialState={{
					pinnedColumns: { left: ['empty', '__check__', 'id', 'actions', '__detail_panel_toggle__'] },
				}}
				rows={list}
				columns={columnsWithSavedWidth}
				onColumnWidthChange={handleColumnWidthChange}
				hideFooterPagination
				hideFooter
				rowHeight={40}
				columnHeaderHeight={40}
				loading={isLoading}
				localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
				className="no-border"
				sortingMode="server"
				onSortModelChange={handleSortChange}
				filterMode="server"
				filterDebounceMs={300}
				onFilterModelChange={handleFilterChange}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
				checkboxSelection={Boolean(!row.process)}
				onRowSelectionModelChange={selection => setSelectedRows(selection)}
				getDetailPanelContent={getDetailPanelContent}
				detailPanelExpandedRowIds={detailPanelExpandedRowIds}
				onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
				getDetailPanelHeight={({ row }) => getDetailPanelHeight(row)}
				disableMultipleColumnsSorting
				disableRowSelectionOnClick
			/>

			{contextComponent}
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	ordersGroupList: getCollectionGroupListSelector(),
	ordersWayList: getCollectionWayListSelector(),
	isCollectionGroupListLoading: getIsCollectionGroupListLoadingSelector(),
	isCollectionWayListLoading: getIsCollectionWayListLoadingSelector(),
});

const mapDispatchToProps = {
	onGetCollectionGroupList: getCollectionGroupListAction,
	onGetCollectionWayList: getCollectionWayListAction,
	onSetGroupOpen: setGroupOpenAction,
	onSetWayOpen: setWayOpenAction,
};

export const CollectionComponent = connect(mapStateToProps, mapDispatchToProps)(CollectionWrapper);

CollectionWrapper.propTypes = {
	row: PropTypes.object.isRequired,
	onGetCollectionGroupList: PropTypes.func,
	onGetCollectionWayList: PropTypes.func,
	ordersGroupList: PropTypes.array,
	ordersWayList: PropTypes.array,
	isCollectionGroupListLoading: PropTypes.array.isRequired,
	isCollectionWayListLoading: PropTypes.array.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	onResetState: PropTypes.func,
	type: PropTypes.string.isRequired,
	onSetGroupOpen: PropTypes.func.isRequired,
	onSetWayOpen: PropTypes.func.isRequired,
};
